import { useEffect, useState, useRef, useCallback } from 'react';
import * as duckdb from '@duckdb/duckdb-wasm';

interface LoadingProgress {
    stage: 'idle' | 'downloading' | 'parsing' | 'indexing' | 'complete';
    bytesLoaded: number;
    bytesTotal: number | null;
    percent: number | null;
}

interface UseDuckDBResult {
    db: duckdb.AsyncDuckDB | null;
    conn: duckdb.AsyncDuckDBConnection | null;
    isReady: boolean;
    isLoading: boolean;
    loadingProgress: LoadingProgress;
    error: string | null;
    rowCount: number | null;
    columns: string[];
    loadCsv: (url: string, tableName?: string) => Promise<void>;
    executeQuery: (sql: string) => Promise<Record<string, unknown>[]>;
}

/**
 * Hook to initialize and manage a DuckDB-WASM instance.
 * Supports loading CSV files from URLs and executing SQL queries.
 */
export function useDuckDB(): UseDuckDBResult {
    const [db, setDb] = useState<duckdb.AsyncDuckDB | null>(null);
    const [conn, setConn] = useState<duckdb.AsyncDuckDBConnection | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState<LoadingProgress>({
        stage: 'idle',
        bytesLoaded: 0,
        bytesTotal: null,
        percent: null,
    });
    const [error, setError] = useState<string | null>(null);
    const [rowCount, setRowCount] = useState<number | null>(null);
    const [columns, setColumns] = useState<string[]>([]);
    const initializingRef = useRef(false);

    useEffect(() => {
        // Prevent double initialization in React Strict Mode
        if (initializingRef.current) {
            return;
        }
        initializingRef.current = true;

        const initDuckDB = async () => {
            try {
                console.log('DuckDB: Starting initialization...');

                // Select the best bundle for the browser
                const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
                const bundle = await duckdb.selectBundle(JSDELIVR_BUNDLES);

                // Create worker and logger
                const worker_url = URL.createObjectURL(
                    new Blob([`importScripts("${bundle.mainWorker}");`], { type: 'text/javascript' })
                );
                const worker = new Worker(worker_url);
                const logger = new duckdb.ConsoleLogger();

                // Instantiate DuckDB
                const dbInstance = new duckdb.AsyncDuckDB(logger, worker);
                await dbInstance.instantiate(bundle.mainModule, bundle.pthreadWorker);

                // Create a connection
                const connection = await dbInstance.connect();

                setDb(dbInstance);
                setConn(connection);
                setIsReady(true);
                console.log('DuckDB: Initialization complete!');
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to initialize DuckDB';
                console.error('DuckDB: Initialization failed:', errorMessage);
                setError(errorMessage);
            }
        };

        initDuckDB();

        // Cleanup on unmount
        return () => {
            if (conn) {
                conn.close();
            }
            if (db) {
                console.log('DuckDB: Cleaning up...');
                db.terminate();
            }
        };
    }, []);

    /**
     * Load a CSV file from a URL into DuckDB.
     * @param url - The presigned URL to the CSV file
     * @param tableName - Name for the table (default: 'data')
     */
    const loadCsv = useCallback(async (url: string, tableName: string = 'data') => {
        if (!conn || !db) {
            throw new Error('DuckDB connection not ready');
        }

        setIsLoading(true);
        setError(null);
        setLoadingProgress({
            stage: 'downloading',
            bytesLoaded: 0,
            bytesTotal: null,
            percent: null,
        });

        try {
            console.log(`DuckDB: Fetching CSV from ${url.substring(0, 50)}...`);

            // Fetch the CSV content with progress tracking
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch CSV: ${response.status} ${response.statusText}`);
            }
            
            // Get total size if available
            const contentLength = response.headers.get('Content-Length');
            const total = contentLength ? parseInt(contentLength, 10) : null;
            
            // Read the response body with progress tracking
            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error('Failed to read response body');
            }
            
            const chunks: Uint8Array[] = [];
            let bytesReceived = 0;
            
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                chunks.push(value);
                bytesReceived += value.length;
                
                setLoadingProgress({
                    stage: 'downloading',
                    bytesLoaded: bytesReceived,
                    bytesTotal: total,
                    percent: total ? Math.round((bytesReceived / total) * 100) : null,
                });
            }
            
            // Combine chunks into single array
            const allChunks = new Uint8Array(bytesReceived);
            let position = 0;
            for (const chunk of chunks) {
                allChunks.set(chunk, position);
                position += chunk.length;
            }
            
            const csvContent = new TextDecoder().decode(allChunks);
            console.log(`DuckDB: Fetched ${csvContent.length} bytes`);
            
            // Update progress to parsing stage
            setLoadingProgress({
                stage: 'parsing',
                bytesLoaded: bytesReceived,
                bytesTotal: total,
                percent: 100,
            });

            // Register the CSV content as a file in DuckDB's virtual filesystem
            const fileName = 'data.csv';
            await db.registerFileText(fileName, csvContent);

            // Update progress to indexing stage
            setLoadingProgress(prev => ({ ...prev, stage: 'indexing' }));

            // Drop existing table if exists
            await conn.query(`DROP TABLE IF EXISTS ${tableName}`);

            // Load from the registered file
            await conn.query(`
                CREATE TABLE ${tableName} AS 
                SELECT * FROM read_csv_auto('${fileName}')
            `);

            // Get row count
            const countResult = await conn.query(`SELECT COUNT(*) as count FROM ${tableName}`);
            const countData = countResult.toArray();
            const count = countData[0]?.count ?? 0;
            setRowCount(Number(count));

            // Get column names
            const schemaResult = await conn.query(`DESCRIBE ${tableName}`);
            const schemaData = schemaResult.toArray();
            const columnNames = schemaData.map((row: Record<string, unknown>) => String(row.column_name || row.name || ''));
            setColumns(columnNames);

            console.log(`DuckDB: Loaded ${count} rows with columns: ${columnNames.join(', ')}`);
            
            // Update progress to complete
            setLoadingProgress(prev => ({ ...prev, stage: 'complete' }));
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load CSV';
            console.error('DuckDB: CSV load failed:', errorMessage);
            setError(errorMessage);
            throw err;
        } finally {
            setIsLoading(false);
            // Reset progress after a short delay to allow UI to show completion
            setTimeout(() => {
                setLoadingProgress({
                    stage: 'idle',
                    bytesLoaded: 0,
                    bytesTotal: null,
                    percent: null,
                });
            }, 500);
        }
    }, [conn, db]);

    /**
     * Execute a SQL query and return results as array of objects.
     * @param sql - The SQL query to execute
     * @returns Array of row objects
     */
    const executeQuery = useCallback(async (sql: string): Promise<Record<string, unknown>[]> => {
        if (!conn) {
            throw new Error('DuckDB connection not ready');
        }

        try {
            console.log(`DuckDB: Executing query: ${sql.substring(0, 100)}...`);
            const result = await conn.query(sql);
            
            // Get column names from schema
            const columnNames = result.schema.fields.map(f => f.name);
            
            // Convert Arrow rows to plain JavaScript objects
            // Arrow row proxies don't spread correctly, so we manually extract values
            const rows = result.toArray();
            const data = rows.map((row) => {
                const obj: Record<string, unknown> = {};
                for (const colName of columnNames) {
                    // Convert BigInt to Number if needed (DuckDB often returns BigInt for integers)
                    const value = (row as Record<string, unknown>)[colName];
                    obj[colName] = typeof value === 'bigint' ? Number(value) : value;
                }
                return obj;
            });
            
            console.log(`DuckDB: Query returned ${data.length} rows`);
            return data;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Query execution failed';
            console.error('DuckDB: Query failed:', errorMessage);
            throw err;
        }
    }, [conn]);

    return { 
        db, 
        conn,
        isReady, 
        isLoading,
        loadingProgress,
        error,
        rowCount,
        columns,
        loadCsv,
        executeQuery
    };
}

export default useDuckDB;
